import { useMemo } from 'react';
import { BoxGeometry, ExtrudeGeometry } from 'three';
import { squareTubeProfile } from './crossSections';
import { SCALE_IN_TO_UNIT, type PattePlacement } from '../types';
import { MATERIALS } from '../materials';

interface Props {
  placement: PattePlacement;
}

/**
 * Patte = X-brace support under a tread.
 * Two vertical square-tube legs spanning the tread width,
 * connected by an X cross-brace.
 */
export function Patte({ placement }: Props) {
  const { widthIn, heightIn } = placement;
  const partNumber = placement.partNumber ?? 'F04003';

  const legGeometry = useMemo(() => {
    const g = new ExtrudeGeometry(squareTubeProfile(), {
      depth: heightIn,
      bevelEnabled: false,
    });
    g.scale(SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT);
    // FIX: extrusion DOWN from group origin (= top of patte = floor surface)
    // toward y=0 (ground). Connects with footpads at y=-heightIn.
    // rotateX(+π/2) maps Z+ extrude axis to Y- (down). Previously was -π/2 (up).
    g.rotateX(Math.PI / 2);
    return g;
  }, [heightIn]);

  // Cross-brace = same square tube extruded along its slope direction
  const braceLength = Math.sqrt(widthIn * widthIn + heightIn * heightIn);
  const braceAngle = Math.atan2(heightIn, widthIn);
  const braceGeometry = useMemo(() => {
    const g = new ExtrudeGeometry(squareTubeProfile(), {
      depth: braceLength,
      bevelEnabled: false,
    });
    g.translate(0, 0, -braceLength / 2);
    g.scale(SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT);
    return g;
  }, [braceLength]);

  // FOOTPAD : carré 8"×8" × 0.5" thick aluminium plate, on ground
  const FOOTPAD_SIZE_IN = 8;
  const FOOTPAD_THICKNESS_IN = 0.5;
  const footpadGeometry = useMemo(() => {
    return new BoxGeometry(
      FOOTPAD_SIZE_IN * SCALE_IN_TO_UNIT,
      FOOTPAD_THICKNESS_IN * SCALE_IN_TO_UNIT,
      FOOTPAD_SIZE_IN * SCALE_IN_TO_UNIT,
    );
  }, []);

  const halfW = (widthIn / 2) * SCALE_IN_TO_UNIT;
  const midH = -(heightIn / 2) * SCALE_IN_TO_UNIT; // brace at mid-height (negative because pattes go DOWN from tread)

  return (
    <group
      position={[placement.position.x, placement.position.y, placement.position.z]}
      rotation={[0, placement.rotationY, 0]}
      userData={{ partNumber, kind: 'patte', refId: placement.id }}
    >
      {/* Left leg */}
      <mesh
        geometry={legGeometry}
        material={MATERIALS.aluminumBrushed}
        position={[-halfW, 0, 0]}
        castShadow
        userData={{ partNumber, kind: 'patte-leg', refId: `${placement.id}-leg-left` }}
      />
      {/* Right leg */}
      <mesh
        geometry={legGeometry}
        material={MATERIALS.aluminumBrushed}
        position={[halfW, 0, 0]}
        castShadow
        userData={{ partNumber, kind: 'patte-leg', refId: `${placement.id}-leg-right` }}
      />
      {/* X-brace diagonal 1 (top-left to bottom-right) */}
      <mesh
        geometry={braceGeometry}
        material={MATERIALS.aluminumBrushed}
        position={[0, midH, 0]}
        rotation={[0, Math.PI / 2, braceAngle]}
        castShadow
        userData={{ partNumber, kind: 'patte-brace', refId: `${placement.id}-brace-1` }}
      />
      {/* X-brace diagonal 2 (top-right to bottom-left) */}
      <mesh
        geometry={braceGeometry}
        material={MATERIALS.aluminumBrushed}
        position={[0, midH, 0]}
        rotation={[0, Math.PI / 2, -braceAngle]}
        castShadow
        userData={{ partNumber, kind: 'patte-brace', refId: `${placement.id}-brace-2` }}
      />
      {/* Footpad left leg */}
      <mesh
        geometry={footpadGeometry}
        material={MATERIALS.aluminumBrushed}
        position={[
          -halfW,
          -heightIn * SCALE_IN_TO_UNIT + (FOOTPAD_THICKNESS_IN / 2) * SCALE_IN_TO_UNIT,
          0,
        ]}
        castShadow
        receiveShadow
        userData={{ partNumber, kind: 'patte-footpad', refId: `${placement.id}-footpad-left` }}
      />
      {/* Footpad right leg */}
      <mesh
        geometry={footpadGeometry}
        material={MATERIALS.aluminumBrushed}
        position={[
          halfW,
          -heightIn * SCALE_IN_TO_UNIT + (FOOTPAD_THICKNESS_IN / 2) * SCALE_IN_TO_UNIT,
          0,
        ]}
        castShadow
        receiveShadow
        userData={{ partNumber, kind: 'patte-footpad', refId: `${placement.id}-footpad-right` }}
      />
    </group>
  );
}
